import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Stack,
  Divider,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {IntercomDeviceLog} from 'src/apis/autoGenerate/types/intercomDeviceLog';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import placeholderCallImg from '../../assets/placeholder_call_event.png';
import placeholderDoorImg from '../../assets/placeholder_door_event.png';
import {StatusBadge, getEventStatusVariant} from 'src/components/ui';
import {neutral} from 'src/theme';

dayjs.extend(utc);
dayjs.extend(timezone);

interface EventDetailsProps {
  open: boolean;
  onClose: () => void;
  event: IntercomDeviceLog | null;
  eventType: 'call' | 'door';
}

function DetailRow({label, value}: {label: string; value: string}) {
  return (
    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75}}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value}
      </Typography>
    </Box>
  );
}

function SectionHeader({icon, title}: {icon: React.ReactNode; title: string}) {
  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
      {icon}
      <Typography variant="subtitle2" fontWeight={600}>
        {title}
      </Typography>
    </Box>
  );
}

export default function EventDetails({open, onClose, event, eventType}: EventDetailsProps) {
  if (!event) return null;

  const placeholderImg = eventType === 'call' ? placeholderCallImg : placeholderDoorImg;
  const photoUrl = event.photo?.photoUrl || placeholderImg;
  const hasRealPhoto = !!event.photo?.photoUrl;

  const formatDateTime = (dateTime: string, timeZone?: string) => {
    if (!dateTime) return {date: '-', time: '-'};
    try {
      const tz = timeZone || event.device?.timezone || 'UTC';
      const localTime = dayjs.utc(dateTime).tz(tz);
      return {
        date: localTime.format('MMM DD, YYYY'),
        time: localTime.format('hh:mm A'),
      };
    } catch {
      const fallbackTime = dayjs.utc(dateTime);
      return {
        date: fallbackTime.format('MMM DD, YYYY'),
        time: fallbackTime.format('hh:mm A'),
      };
    }
  };

  const dateTimeInfo = formatDateTime(event.created_at, event.device?.timezone);

  const handleDownload = async () => {
    if (!hasRealPhoto) return;
    try {
      const response = await fetch(photoUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `event-${event.id}-${event.created_at}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download image:', error);
    }
  };

  const getEventTypeText = () => {
    if (eventType === 'call') return 'Call Event';
    const operationType = event.operation_type;
    if (operationType === 'VIRTUAL_PIN_VERIFY') {
      const pinType = event.metadata?.pinType;
      switch (pinType) {
        case 'room':
          return 'Room PIN Access';
        case 'delivery':
          return 'Delivery Access';
        case 'guest':
          return 'Guest Access';
        default:
          return 'Virtual Pin Access';
      }
    }
    return 'Door Open';
  };

  const getUserName = () => {
    if (event.entrio_users) {
      return `${event.entrio_users.first_name} ${event.entrio_users.last_name}`;
    }
    if (event.operation_type === 'VIRTUAL_PIN_VERIFY' && event.metadata) {
      if (event.metadata.deliveryName) return event.metadata.deliveryName;
      if (event.metadata.guestName) return event.metadata.guestName.trim();
      if (event.metadata.roomName) return `Room ${event.metadata.roomName}`;
    }
    return '-';
  };

  const statusVariant = getEventStatusVariant(event.status);
  const statusLabel = (event.status || '').toUpperCase();

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: {xs: '100vw', sm: 420},
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 2,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Event Details
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{flex: 1, overflow: 'auto', px: 2.5, pb: 3}}>
          <Stack spacing={2.5}>
            {/* Photo + Status */}
            <Box>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  bgcolor: neutral[100],
                  borderRadius: 2,
                  overflow: 'hidden',
                  minHeight: 260,
                }}
              >
                <img
                  src={photoUrl}
                  alt="Event"
                  style={{
                    width: '100%',
                    maxHeight: 320,
                    objectFit: 'cover',
                  }}
                  onError={e => {
                    (e.target as HTMLImageElement).src = placeholderImg;
                  }}
                />
              </Box>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5}}>
                <StatusBadge status={statusVariant} label={statusLabel} />
                <Typography variant="subtitle1" fontWeight={600}>
                  {getEventTypeText()}
                </Typography>
              </Box>
            </Box>

            {/* User Section */}
            <Box>
              <SectionHeader
                icon={<PersonOutlineIcon sx={{fontSize: 18, color: 'text.secondary'}} />}
                title="User"
              />
              <Divider sx={{mb: 1}} />
              <DetailRow label="User" value={getUserName()} />
              {event.entrio_users?.email && event.entrio_users.email !== '-' && (
                <DetailRow label="Email" value={event.entrio_users.email} />
              )}
              {eventType === 'call' && event.metadata?.roomName && (
                <DetailRow label="Unit" value={event.metadata.roomName} />
              )}
              {eventType === 'door' && event.metadata?.accessMethod && (
                <DetailRow
                  label="Method"
                  value={
                    event.metadata.accessMethod === 'FRONT_DESK'
                      ? 'Front Desk'
                      : event.metadata.accessMethod
                  }
                />
              )}
              {(event.metadata?.source === 'FRONT_DESK' ||
                event.metadata?.accessMethod === 'FRONT_DESK' ||
                event.metadata?.callType === 'FRONT_DESK') && (
                <DetailRow label="Source" value="Front Desk" />
              )}
            </Box>

            {/* Device Section */}
            <Box>
              <SectionHeader
                icon={<DevicesOtherIcon sx={{fontSize: 18, color: 'text.secondary'}} />}
                title="Device"
              />
              <Divider sx={{mb: 1}} />
              <DetailRow label="Device" value={event.device?.name || '-'} />
              {event.device?.apartment && (
                <DetailRow label="Location" value={event.device.apartment} />
              )}
            </Box>

            {/* Time Section */}
            <Box>
              <SectionHeader
                icon={<AccessTimeIcon sx={{fontSize: 18, color: 'text.secondary'}} />}
                title="Time"
              />
              <Divider sx={{mb: 1}} />
              <DetailRow
                label="Time"
                value={`${dateTimeInfo.date} - ${dateTimeInfo.time}`}
              />
            </Box>

            {/* Action Button */}
            <Box sx={{pt: 1}}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                disabled={!hasRealPhoto}
                fullWidth
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Download Photo
              </Button>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
}
